/**
 * dq-variant-offcanvas.js
 * Moon Magic-style variant + gemstone drawer for Divine Quartz
 *
 * Key architecture note:
 * Drawers are rendered inside product-info in the Liquid template, but Dawn's
 * scroll-reveal animation applies CSS transform to the product info wrapper.
 * Any position:fixed element inside a transformed ancestor gets a broken
 * fixed-positioning context. We fix this by moving all .dq-drawer elements
 * to document.body on initialisation.
 */

(function () {
  'use strict';

  /* ── Global body-level overlay ────────────────────────────────────── */

  var overlay = document.getElementById('dq-global-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'dq-global-overlay';
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'background:rgba(0,0,0,0.52)',
      'z-index:9998',
      'opacity:0',
      'visibility:hidden',
      'pointer-events:none',
      'transition:opacity 0.3s ease, visibility 0.3s ease',
    ].join(';');
    document.body.appendChild(overlay);
  }

  var activeDrawer = null;
  var lastFocusTarget = null; // tracks opener button for focus restoration

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  }

  function showOverlay() {
    overlay.style.opacity = '1';
    overlay.style.visibility = 'visible';
    overlay.style.pointerEvents = 'all';
  }

  function hideOverlay() {
    overlay.style.opacity = '0';
    overlay.style.visibility = 'hidden';
    overlay.style.pointerEvents = 'none';
  }

  // Returns all keyboard-focusable elements within a container
  function getFocusable(container) {
    return Array.from(container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]),' +
      'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  }

  function openDrawerEl(drawer, openerEl) {
    if (activeDrawer) closeActiveDrawer();
    lastFocusTarget = openerEl || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    showOverlay();
    lockScroll(true);
    activeDrawer = drawer;
    // Move focus to the close button
    var closeBtn = drawer.querySelector('[data-dq-drawer-close]');
    if (closeBtn) requestAnimationFrame(function () { closeBtn.focus(); });
  }

  function closeActiveDrawer() {
    if (!activeDrawer) return;
    activeDrawer.classList.remove('is-open');
    activeDrawer.setAttribute('aria-hidden', 'true');
    var toFocus = lastFocusTarget;
    activeDrawer = null;
    lastFocusTarget = null;
    hideOverlay();
    lockScroll(false);
    // Restore focus to the element that opened the drawer
    if (toFocus && typeof toFocus.focus === 'function') {
      requestAnimationFrame(function () { toFocus.focus(); });
    }
  }

  // Close on overlay click
  overlay.addEventListener('click', closeActiveDrawer);

  // Keyboard handling: Escape closes, Tab stays trapped inside open drawer
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeActiveDrawer();
      return;
    }
    if (e.key !== 'Tab' || !activeDrawer) return;
    var focusable = getFocusable(activeDrawer);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ── Per product-info initialisation ──────────────────────────────── */

  document.querySelectorAll('product-info').forEach(function (productInfo) {
    var sectionId = productInfo.getAttribute('data-section');
    var nativePickerWrap = productInfo.querySelector('.dq-native-variant-picker');
    var optionRowsRoot = productInfo.querySelector('[data-dq-opts]');

    /* ── Move all drawers to document.body ──
       This escapes any CSS transform on the product info wrapper that would
       break position:fixed child elements.                                  */
    productInfo.querySelectorAll('.dq-drawer').forEach(function (drawer) {
      document.body.appendChild(drawer);
      // Wire close buttons directly (they're no longer inside productInfo)
      drawer.querySelectorAll('[data-dq-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', closeActiveDrawer);
      });
    });

    // Also move any drawers rendered outside product-info (e.g. info_tabs drawers
    // that are siblings of the product-info element in the DOM)
    var productInfoParent = productInfo.parentElement;
    if (productInfoParent) {
      productInfoParent.querySelectorAll('.dq-drawer').forEach(function (drawer) {
        document.body.appendChild(drawer);
        drawer.querySelectorAll('[data-dq-drawer-close]').forEach(function (btn) {
          btn.addEventListener('click', closeActiveDrawer);
        });
      });
    }

    /* ── Product meta (variants JSON for direct media switching) ───── */
    var productMeta = null;
    var metaEl = productInfo.querySelector('[data-dq-product-meta]');
    if (metaEl) {
      try {
        productMeta = JSON.parse(metaEl.textContent);
      } catch (e) {
        console.warn('[DQ] Could not parse product meta JSON — variant image switching disabled.', e);
      }
    }

    /* ── Variant picker (only if native picker exists) ─────────────── */
    if (!nativePickerWrap || !optionRowsRoot) {
      // Still wire generic open-drawer buttons (e.g. gemstone selector)
      wireOpenDrawerButtons(productInfo);
      return;
    }

    function getCurrentValue(optionName) {
      var escaped = CSS.escape(optionName);
      var radios = Array.from(
        nativePickerWrap.querySelectorAll(
          'input[type="radio"][name="options[' + escaped + ']"]'
        )
      );
      if (radios.length) {
        var checked = radios.find(function (r) { return r.checked; });
        return checked ? checked.value : '';
      }
      var sel = nativePickerWrap.querySelector(
        'select[name="options[' + escaped + ']"]'
      );
      return sel ? sel.value : '';
    }

    function setNativeValue(optionName, value) {
      var escaped = CSS.escape(optionName);
      var radios = Array.from(
        nativePickerWrap.querySelectorAll(
          'input[type="radio"][name="options[' + escaped + ']"]'
        )
      );
      if (radios.length) {
        var target = radios.find(function (r) { return r.value === value; });
        if (target) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      var sel = nativePickerWrap.querySelector(
        'select[name="options[' + escaped + ']"]'
      );
      if (sel) {
        sel.value = value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ── Directly switch gallery media when a variant is chosen ────── */
    function switchVariantMedia() {
      if (!productMeta || !productMeta.variants || !productMeta.options) return;
      var optionNames = productMeta.options;
      var currentValues = optionNames.map(function (name) {
        return getCurrentValue(name);
      });
      var matched = null;
      for (var i = 0; i < productMeta.variants.length; i++) {
        var v = productMeta.variants[i];
        if (v.options && v.options.every(function (val, idx) { return val === currentValues[idx]; })) {
          matched = v;
          break;
        }
      }
      if (!matched || !matched.featured_media || !matched.featured_media.id) return;
      var variantInput = productInfo.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = matched.id;
      var gallery = document.querySelector('media-gallery[id="MediaGallery-' + sectionId + '"]');
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(sectionId + '-' + matched.featured_media.id, true);
      }
    }

    function syncUI() {
      // Sync text labels
      optionRowsRoot
        .querySelectorAll('[data-dq-selected-label]')
        .forEach(function (span) {
          var optIdx = span.getAttribute('data-dq-selected-label');
          var row = optionRowsRoot.querySelector('[data-option-index="' + optIdx + '"]');
          if (!row) return;
          var optName = row.getAttribute('data-option-name');
          if (!optName) return;
          span.textContent = getCurrentValue(optName);
        });

      // Sync row thumbnails
      optionRowsRoot
        .querySelectorAll('[data-dq-row-thumb]')
        .forEach(function (thumbDiv) {
          var optIdx = thumbDiv.getAttribute('data-dq-row-thumb');
          var row = optionRowsRoot.querySelector('[data-option-index="' + optIdx + '"]');
          if (!row) return;
          var optName = row.getAttribute('data-option-name');
          if (!optName) return;
          var val = getCurrentValue(optName);
          var drawer = document.getElementById('dq-drawer-' + sectionId + '-' + optIdx);
          if (!drawer) return;
          var matchingOpt = drawer.querySelector(
            '[data-option-value="' + CSS.escape(val) + '"]'
          );
          if (!matchingOpt) return;
          var imgSrc = matchingOpt.getAttribute('data-img-src');
          if (!imgSrc) return;
          var img = thumbDiv.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.width = 60;
            img.height = 60;
            thumbDiv.appendChild(img);
          }
          img.src = imgSrc;
          img.alt = val;
        });

      // Drawer option active states
      document.querySelectorAll(
        '[id^="dq-drawer-' + sectionId + '"] .dq-drawer__opt'
      ).forEach(function (opt) {
        var optName = opt.getAttribute('data-option-name');
        var optValue = opt.getAttribute('data-option-value');
        if (!optName || !optValue) return;
        opt.classList.toggle('is-active', getCurrentValue(optName) === optValue);
      });

      // Size button active states
      optionRowsRoot.querySelectorAll('.dq-size-btn').forEach(function (btn) {
        var optName = btn.getAttribute('data-option-name');
        var btnValue = btn.getAttribute('data-dq-size-value');
        if (!optName || !btnValue) return;
        btn.classList.toggle('is-active', getCurrentValue(optName) === btnValue);
      });
    }

    // Open variant drawer (by option index)
    productInfo.addEventListener('click', function (e) {
      // Variant option row
      var opener = e.target.closest('[data-dq-open]');
      if (opener) {
        var optIdx = opener.getAttribute('data-dq-open');
        var drawer = document.getElementById('dq-drawer-' + sectionId + '-' + optIdx);
        if (drawer) openDrawerEl(drawer, opener);
        return;
      }

      // Drawer option selected (variant) — handles clicks that still bubble to productInfo
      var drawerOpt = e.target.closest('[data-dq-pick-opt]');
      if (drawerOpt) {
        var optName = drawerOpt.getAttribute('data-option-name');
        var optValue = drawerOpt.getAttribute('data-option-value');
        if (optName && optValue) {
          setNativeValue(optName, optValue);
          switchVariantMedia();
          syncUI();
          closeActiveDrawer();
        }
        return;
      }

      // Inline size button
      var sizeBtn = e.target.closest('[data-dq-size-value]');
      if (sizeBtn) {
        if (sizeBtn.disabled || sizeBtn.classList.contains('is-sold-out')) return;
        var szName = sizeBtn.getAttribute('data-option-name');
        var szValue = sizeBtn.getAttribute('data-dq-size-value');
        if (szName && szValue) {
          setNativeValue(szName, szValue);
          switchVariantMedia();
          syncUI();
        }
        return;
      }
    });

    // Sync when Dawn's native picker changes externally
    nativePickerWrap.addEventListener('change', syncUI);

    syncUI();

    // Wire [data-dq-pick-opt] directly on each variant drawer.
    // After document.body.appendChild(drawer), clicks no longer bubble
    // back to productInfo, so delegation on productInfo misses them.
    document.querySelectorAll('[id^="dq-drawer-' + sectionId + '"]').forEach(function (drawer) {
      drawer.addEventListener('click', function (e) {
        var drawerOpt = e.target.closest('[data-dq-pick-opt]');
        if (!drawerOpt) return;
        var optName = drawerOpt.getAttribute('data-option-name');
        var optValue = drawerOpt.getAttribute('data-option-value');
        if (optName && optValue) {
          setNativeValue(optName, optValue);
          switchVariantMedia();
          syncUI();
          closeActiveDrawer();
        }
      });
    });

    // Wire generic open-drawer buttons (gemstone + any future drawers)
    wireOpenDrawerButtons(productInfo);
    if (productInfoParent) wireOpenDrawerButtons(productInfoParent);
  });

  /* ── Generic open-drawer handler (used for gemstone etc.) ─────────── */

  function wireOpenDrawerButtons(root) {
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-dq-open-drawer]');
      if (!btn) return;
      var drawerId = btn.getAttribute('data-dq-open-drawer');
      var drawer = document.getElementById(drawerId);
      if (drawer) openDrawerEl(drawer, btn);
    });
  }

})();
